import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  Film, Plus, FolderKanban, Users, Calendar, MoreVertical,
  Edit, Trash2, ExternalLink, ArrowLeft, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Projects = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    project_type: 'commercial',
    production_house: '',
    deadline: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/projects`, { 
        headers, 
        withCredentials: true 
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/projects`, newProject, { 
        headers, 
        withCredentials: true 
      });
      setProjects(prev => [response.data, ...prev]);
      setShowCreateModal(false);
      setNewProject({
        name: '',
        description: '',
        project_type: 'commercial',
        production_house: '',
        deadline: ''
      });
    } catch (error) {
      console.error('Error creating project:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/projects/${projectId}`, { 
        headers, 
        withCredentials: true 
      });
      setProjects(prev => prev.filter(p => p.project_id !== projectId));
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const projectTypes = [
    { value: 'commercial', label: 'Commercial' },
    { value: 'film', label: 'Film' },
    { value: 'web_series', label: 'Web Series' },
    { value: 'ad', label: 'Advertisement' },
    { value: 'music_video', label: 'Music Video' },
    { value: 'short_film', label: 'Short Film' }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 nav-glass">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Dashboard</span>
            </button>

            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary flex items-center justify-center">
                <Film className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="font-display text-xl tracking-wide">CASTDECK</span>
            </Link>

            <Button onClick={() => setShowCreateModal(true)} className="btn-gold" data-testid="create-project-btn">
              <Plus className="w-4 h-4 mr-2" /> New Project
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Projects</h1>
          <p className="text-muted-foreground">Manage your casting projects and shortlists</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full spinner"></div>
          </div>
        ) : projects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <div 
                key={project.project_id} 
                className="project-card group"
                data-testid={`project-card-${project.project_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center">
                    <FolderKanban className="w-6 h-6 text-primary" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-white/5 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`project-menu-${project.project_id}`}>
                        <MoreVertical className="w-5 h-5 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-card border-border">
                      <DropdownMenuItem onClick={() => navigate(`/projects/${project.project_id}`)}>
                        <ExternalLink className="w-4 h-4 mr-2" /> View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDeleteProject(project.project_id)} className="text-destructive">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <Link to={`/projects/${project.project_id}`}>
                  <h3 className="text-xl font-semibold mb-2 hover:text-primary transition-colors">{project.name}</h3>
                </Link>
                
                {project.description && (
                  <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{project.description}</p>
                )}

                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <span className="px-2 py-1 bg-muted capitalize">{project.project_type.replace('_', ' ')}</span>
                  {project.production_house && <span>{project.production_house}</span>}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">{project.shortlisted_talents?.length || 0} shortlisted</span>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium uppercase ${
                    project.status === 'active' ? 'bg-green-500/10 text-green-400' : 'bg-muted text-muted-foreground'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state py-24 bg-card border border-border">
            <FolderKanban className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <h3 className="text-xl font-semibold mb-2">No projects yet</h3>
            <p className="text-muted-foreground mb-6">Create your first casting project to get started</p>
            <Button onClick={() => setShowCreateModal(true)} className="btn-gold" data-testid="create-first-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Create Project
            </Button>
          </div>
        )}
      </main>

      {/* Create Project Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-card border-border sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl">Create New Project</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Set up a new casting project to start shortlisting talent.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-sm font-medium mb-2 block">Project Name *</Label>
              <Input
                id="name"
                value={newProject.name}
                onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Ad Campaign 2024"
                className="bg-input/20 border-white/10"
                required
                data-testid="project-name-input"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium mb-2 block">Description</Label>
              <Textarea
                id="description"
                value={newProject.description}
                onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the project..."
                className="bg-input/20 border-white/10"
                rows={3}
                data-testid="project-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="type" className="text-sm font-medium mb-2 block">Project Type</Label>
                <Select 
                  value={newProject.project_type} 
                  onValueChange={(v) => setNewProject(prev => ({ ...prev, project_type: v }))}
                >
                  <SelectTrigger className="bg-input/20 border-white/10" data-testid="project-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="deadline" className="text-sm font-medium mb-2 block">Deadline</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={newProject.deadline}
                  onChange={(e) => setNewProject(prev => ({ ...prev, deadline: e.target.value }))}
                  className="bg-input/20 border-white/10"
                  data-testid="project-deadline-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="production_house" className="text-sm font-medium mb-2 block">Production House</Label>
              <Input
                id="production_house"
                value={newProject.production_house}
                onChange={(e) => setNewProject(prev => ({ ...prev, production_house: e.target.value }))}
                placeholder="e.g., Dharma Productions"
                className="bg-input/20 border-white/10"
                data-testid="project-production-input"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateModal(false)} 
                className="border-white/10"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="btn-gold" 
                disabled={creating || !newProject.name}
                data-testid="submit-project-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 spinner mr-2" /> : null}
                Create Project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;
